import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import * as _ from 'lodash-es';
import { TokenService } from '../../services/api/auth/token.service';
import { HeaderForAuthentication } from '../../services/api/auth/model/header-for-authentication.model';


@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {

  private readonly maxRetries = 3;
  private readonly retryDelayMs = 500;

  constructor(private router: Router,
              private tokenService: TokenService) {
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Public
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.headers instanceof HeaderForAuthentication) {
      const accountIds = request.headers.accountIds;

      return this.getAuthToken$(accountIds)
        .pipe(
          switchMap(token => {
            return next.handle(this.addAuthHeader(request, token))
              .pipe(
                catchError(error => {
                  if (this.isUnauthorizedOrForbiddenError(error)) {
                    return this.retryRequest(request, next, accountIds, error, 1);
                  } else {
                    if (error.status === 0) {
                      console.log('STATUS CODE 0. NO INTERNET CONNECTION!');
                    }

                    return throwError(error);
                  }
                }),
              );
          }),
        );

    } else {
      return next.handle(request);
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Private
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  private addAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private isUnauthorizedOrForbiddenError(error: any) {
    return (error instanceof HttpErrorResponse) && (error.status === 401 || error.status === 403);
  }

  private getAuthToken$(accountIds: string[]): Observable<string> {
    if (_.isEmpty(accountIds)) {
      return this.tokenService.getBaseToken$();
    } else {
      return this.tokenService.getAccountToken$(accountIds);
    }
  }

  private retryRequest(request: HttpRequest<any>,
                       next: HttpHandler,
                       accountIds: string[],
                       error: HttpErrorResponse,
                       attempt: number): Observable<HttpEvent<any>> {

    if (attempt <= this.maxRetries && this.isUnauthorizedOrForbiddenError(error)) {
      return timer(this.retryDelayMs)
        .pipe(
          switchMap(() => {
            return this.getAuthToken$(accountIds)
              .pipe(
                switchMap((newToken) => {
                  return next.handle(this.addAuthHeader(request, newToken))
                    .pipe(
                      catchError(retryError => this.retryRequest(request, next, accountIds, retryError, attempt + 1)),
                    );
                }),
              );
          }),
        );
    } else {
      console.log('session timed out. Redirected to login page!');

      // redirect to login page when a call to the backend was rejected with 401 or 403 this.maxRetries times
      this.router.navigate(['/']);

      return throwError(error);
    }
  }
}
